/**
 * /audience — Audience / Targeting drawn natively from Supabase (spec
 * 2026-10-16-native-audience-targeting-design). Composition only: reads in
 * useAudienceData, rows in buildAudienceView, box 1 in AudienceSegments, box 2
 * in AudienceTable, F's Active control in ActiveOnlyToggle. When the channels in
 * view spent nothing (view.empty, rules-04 A3), or the one ticked channel is known
 * not to be connected, spend or not (rules-04 H3), D2's ChannelEmptyState replaces
 * boxes 1 and 2, and the notes about the data stay under it. Period, objective
 * and currency are the dashboards' shared filters (useDashboardFilters); the
 * channel selection is D1's remembered one; Active and "แสดงทั้งหมด" are D4's
 * own URL keys (useAudienceUrl). The scope is D2's (useChannelPageScope,
 * useScopeArrival), so scope=all on a link works here too. Export offers PDF
 * and CSV; the chat runs in mode 'audience' over the two boxes (AudienceChat,
 * spec 2026-10-21 d3-d4 chat §5).
 *
 * @module pages/AudienceTargeting
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActiveOnlyToggle } from '@/components/audience/ActiveOnlyToggle';
import { AudienceNotes } from '@/components/audience/AudienceNotes';
import { AudienceSegments } from '@/components/audience/AudienceSegments';
import { AudienceTable } from '@/components/audience/AudienceTable';
import { YDMPageHero } from '@/components/brand';
import { ChannelEmptyState } from '@/components/channels/ChannelEmptyState';
import { DataStatusPill } from '@/components/looker/DataStatusPill';
import { ExportMenu } from '@/components/looker/ExportMenu';
import MediaChatBubble from '@/components/MediaChatBubble';
import { OverviewFilterBar } from '@/components/overview/OverviewFilterBar';
import { PrintHeader } from '@/components/overview/PrintHeader';
import { AUDIENCE_ADVISOR } from '@/config/agentProfiles';
import { useChannelFilter } from '@/contexts/ChannelFilterContext';
import { useAudienceData, type AudienceData } from '@/hooks/useAudienceData';
import { useAudienceUrl } from '@/hooks/useAudienceUrl';
import { useChannelPageScope, useScopeArrival, type PageScope } from '@/hooks/useChannelPageScope';
import { useDashboardFilters } from '@/hooks/useDashboardFilters';
import { useHeldChatContext } from '@/hooks/useHeldChatContext';
import { PRINT_FRAME_WIDTH_PX, usePrintFrame } from '@/hooks/usePrintFrame';
import { buildAudienceChatContext } from '@/services/audience/audienceChatContext';
import { audienceCsvReady, downloadAudienceCsv } from '@/services/audience/audienceCsv';
import { activeFilterText, dataNotesOf, footnotesOf } from '@/services/audience/audienceText';
import type { AudienceFilters, AudienceView } from '@/services/audience/audienceTypes';
import { audienceDataDateOf, buildAudienceView } from '@/services/audience/audienceView';
import { AD_CHANNEL_IDS } from '@/services/channelFilter';
import { cardEmptyOf, soleChannelLabel } from '@/services/channels/channelFormat';
import { soleChannelConnectedOf } from '@/services/channels/channelView';
import { channelFilterText, objectiveFilterText } from '@/services/creatives/creativeText';
import { csvLabelsFrom } from '@/services/looker/lookerCsv';
import { addDays, bangkokToday, rangeDays } from '@/services/looker/lookerDateRange';
import { DASH } from '@/services/overview/overviewFormat';

const BANNER_CLASS = 'rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-ink-950';
const PRINT_LINE_CLASS = 'hidden text-xs text-neutral-800 print:block';

/**
 * R13, D3's sheet: landscape A4 for this page only, and box 2 fitted to its card on paper (smaller type, tighter cells,
 * headers that wrap); the rows stay whole across pages. The rules leave with the page.
 */
const PRINT_CSS = [
  '@page { size: A4 landscape; margin: 10mm; }',
  '@media print {',
  '  [data-overview-box="2"] table { font-size: 11px; }',
  '  [data-overview-box="2"] th, [data-overview-box="2"] td { padding-left: 6px; padding-right: 6px; }',
  '  [data-overview-box="2"] th { white-space: normal; }',
  '  [data-overview-box="2"] tr { break-inside: avoid; }',
  '}',
].join('\n');

/** D1's remembered channel selection, as display-ordered ids; [] = every channel (as D1's page reads it). */
const useChannels = (): string[] => {
  const { selected, filtering } = useChannelFilter();
  const key = filtering ? AD_CHANNEL_IDS.filter((id) => selected.has(id)).join(',') : '';
  return useMemo(() => (key ? key.split(',') : []), [key]);
};

interface PageState {
  filters: ReturnType<typeof useDashboardFilters>;
  url: ReturnType<typeof useAudienceUrl>;
  audienceFilters: AudienceFilters;
  data: AudienceData;
  view: AudienceView;
  dataDate: string | null;
}

/** R18's order of URL writers, then the reads, then the view. Only the scope and the period reach a read (spec §2.2). */
const useAudiencePage = (page: PageScope): PageState => {
  const filters = useDashboardFilters();
  const url = useAudienceUrl(filters.shared);
  // Last, so the arrival replace is the final writer of the first pass and carries the shared keys; D4's own keys
  // settle one pass later (D3's R4, D2's I2).
  useScopeArrival(page, filters.shared);
  const channels = useChannels();
  const data = useAudienceData(page.scope, filters.shared.period);
  const { objective, currency } = filters.shared;
  const { activeOnly, showAll } = url.audience;
  const audienceFilters = useMemo<AudienceFilters>(
    () => ({ channels, objective, currency, activeOnly, showAll }), [channels, objective, currency, activeOnly, showAll],
  );
  // Bangkok's yesterday on the day the page is open: Active and the chips (spec §4.4), whatever period is shown.
  const yesterday = addDays(bangkokToday(), -1);
  // rules-04 G3: whether the one ticked channel is connected, from the same statuses read; unknown in all workspaces.
  const channelConnected = soleChannelConnectedOf(channels, data.freshness, page.showAll);
  const view = useMemo(
    () => buildAudienceView({ facts: data.facts, filters: audienceFilters, yesterday, channelConnected }),
    [data.facts, audienceFilters, yesterday, channelConnected],
  );
  return { filters, url, audienceFilters, data, view, dataDate: audienceDataDateOf(data.freshness, view) };
};

interface PageExport {
  framing: boolean;
  onPdf: () => void;
  pdfDisabled: boolean;
  onCsv: () => void;
  csvDisabled: boolean;
}

/** R11: both items wait for the table; PDF also for the print frame, CSV also for the freshness read. */
const usePageExport = (page: PageScope, state: PageState): PageExport => {
  const { t } = useTranslation();
  const printFrame = usePrintFrame();
  const labels = useMemo(() => csvLabelsFrom(t), [t]);
  const { data, view, audienceFilters, dataDate } = state;
  const range = data.windows?.current ?? null;
  const onCsv = (): void => {
    if (!range) return;
    downloadAudienceCsv({
      allWorkspaces: page.showAll, workspaceName: page.workspaceName, range, view, filters: audienceFilters,
      freshness: data.freshness, dataDate,
    }, labels, t);
  };
  return {
    framing: printFrame.framing,
    onPdf: printFrame.start,
    pdfDisabled: view.table.status !== 'ready' || printFrame.framing,
    onCsv,
    csvDisabled: range === null || !audienceCsvReady(view, data.freshness),
  };
};

/** The master's switch, as on D1–D3. */
const AllWorkspacesToggle: React.FC<{ checked: boolean; onChange: (on: boolean) => void }> = ({ checked, onChange }) => {
  const { t } = useTranslation();
  return (
    <label className="inline-flex items-center gap-2 text-sm font-medium text-ink-900">
      <input type="checkbox" className="h-4 w-4 rounded border-neutral-400 accent-ink-950" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {t('looker.viewAllWorkspaces')}
    </label>
  );
};

interface PartProps {
  page: PageScope;
  state: PageState;
}

/** Row F (spec §3.2), D3's control band: the filters, the data pill, Export, the switch, the period and Active, the banner. */
const ControlBand: React.FC<PartProps & { exporter: PageExport }> = ({ page, state, exporter }) => {
  const { t } = useTranslation();
  const { filters: { shared, setShared }, url: { audience, setAudience }, data, view, dataDate } = state;
  const range = data.windows?.current;
  return (
    <section data-audience-controls className="print:hidden space-y-4 rounded-2xl border border-neutral-200 border-l-[5px] border-l-primary-500 bg-white px-5 py-4 shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <OverviewFilterBar
          token={shared.period} onToken={(period) => setShared({ period })}
          objective={shared.objective} onObjective={(objective) => setShared({ objective })}
          money={view.money} onCurrency={(currency) => setShared({ currency })}
          disabled={data.facts.status === 'loading'}
        />
        <div className="flex flex-wrap items-center gap-3">
          <DataStatusPill latestDataDate={dataDate} state={data.freshness} />
          <ExportMenu pdfDisabled={exporter.pdfDisabled} onPdf={exporter.onPdf} csvDisabled={exporter.csvDisabled} onCsv={exporter.onCsv} />
          {page.isMaster && <AllWorkspacesToggle checked={page.viewAll} onChange={page.setViewAll} />}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {range && <p className="text-xs text-neutral-700">{t('audience.periodLine', { start: range.start, end: range.end })}</p>}
        <ActiveOnlyToggle on={audience.activeOnly} hidden={view.hidden} onActive={(activeOnly) => setAudience({ activeOnly })} />
      </div>
      {page.showAll && <p role="status" className={BANNER_CLASS}>{t('looker.allWorkspacesBanner')}</p>}
    </section>
  );
};

/** R13: the band does not print, so its period and filters print as two lines, Active included. */
const PrintLines: React.FC<{ state: PageState }> = ({ state }) => {
  const { t } = useTranslation();
  const { data: { windows }, audienceFilters: f, view } = state;
  const range = windows?.current;
  return (
    <>
      {range && <p className={PRINT_LINE_CLASS}>{t('audience.periodLine', { start: range.start, end: range.end })}</p>}
      <p className={PRINT_LINE_CLASS}>
        {t('audience.printFilters', {
          channels: channelFilterText(f.channels, t), objective: objectiveFilterText(f.objective, t),
          currency: view.money?.currency ?? DASH, active: activeFilterText(f.activeOnly, view.hidden, t),
        })}
      </p>
    </>
  );
};

/**
 * Boxes 1 and 2 and the note line for boxes 3–5 (R14), once the page has a scope and a window. Under `view.empty`
 * the card stands in for boxes 1 and 2, and the notes about the data (left out) stay under it; under either card the
 * view counts no unknown currency (rules-04 H3, I2).
 */
const AudienceBody: React.FC<PartProps> = ({ page, state }) => {
  const { t } = useTranslation();
  const { data, view, audienceFilters, url: { audience, setAudience } } = state;
  if (!page.scope) return <p className="text-sm text-neutral-700">{t('looker.loading')}</p>;
  if (!data.windows) return <p className="text-sm text-neutral-800">{t('looker.badDateRange')}</p>;
  const scope = page.showAll ? t('looker.allWorkspaces') : page.workspaceName ?? '';
  const currency = view.money?.currency ?? null;
  const footnotes = { leftOut: view.leftOut, unknownCurrency: view.unknownCurrency };
  return (
    <>
      {view.empty ? (
        <>
          <ChannelEmptyState
            empty={cardEmptyOf(view.empty)} channelLabel={soleChannelLabel(view.empty.channels)}
            windowDays={rangeDays(data.windows.current)}
          />
          <AudienceNotes notes={dataNotesOf(footnotes, t)} />
        </>
      ) : (
        <>
          <AudienceSegments state={view.table} currency={currency} scope={scope} filters={audienceFilters} onRetry={data.retry} />
          <AudienceTable
            state={view.table} currency={currency} scope={scope} filters={audienceFilters} notes={footnotesOf(footnotes, t)}
            showAll={audience.showAll} onShowAll={(on) => setAudience({ showAll: on })} onRetry={data.retry}
          />
        </>
      )}
      <p data-audience-later className="text-xs text-neutral-700">{t('audience.notes.later')}</p>
    </>
  );
};

/**
 * The bubble in mode 'audience' with AUDIENCE_ADVISOR (spec 2026-10-21 d3-d4 chat §5), told once the page settled: a
 * scope, a period, the table read (ready or failed: a chat can say a read failed, a file cannot) and the statuses read.
 * Keyed by its scope, so another workspace or the all-workspaces switch starts a new conversation. A filter, the period
 * or Retry keeps it: while the page has no new data yet (its reads are loading, or it refuses the period) the chat
 * holds the last data and takes no new question.
 */
const AudienceChat: React.FC<PartProps> = ({ page, state }) => {
  const { t } = useTranslation();
  const { data, view, audienceFilters, dataDate } = state;
  const { windows } = data;
  const settled = page.scope !== null && windows !== null && view.table.status !== 'loading' && data.freshness.status !== 'loading';
  const context = useMemo(() => (settled && windows ? buildAudienceChatContext({
    allWorkspaces: page.showAll, workspaceName: page.workspaceName, range: windows.current, previous: windows.previous,
    view, filters: audienceFilters, freshness: data.freshness, dataDate,
  }, t) : null), [settled, windows, page.showAll, page.workspaceName, view, audienceFilters, data.freshness, dataDate, t]);
  const scope = `audience:${page.showAll ? 'all' : page.workspaceId ?? ''}`;
  const held = useHeldChatContext(context, scope);
  if (!held.context) return null;
  return (
    <div className="print:hidden">
      <MediaChatBubble
        key={scope}
        mode="audience"
        lookerContext={held.context}
        contextPending={held.pending}
        scope={scope}
        title={t('audience.chatTitle')}
        subtitle={t('looker.chatSubtitle')}
        guidedQuestions={AUDIENCE_ADVISOR.guidedQuestions}
      />
    </div>
  );
};

const AudienceTargeting: React.FC = () => {
  const { t } = useTranslation();
  const page = useChannelPageScope();
  const state = useAudiencePage(page);
  const exporter = usePageExport(page, state);
  return (
    <div className="space-y-4 p-4 md:p-6 print:p-0">
      <div className="print:hidden">
        <YDMPageHero
          eyebrow={page.showAll ? t('looker.allWorkspaces') : page.workspaceName ?? undefined}
          headline={t('audience.title')} subtitle={t('audience.subtitle')} className="mb-2"
        />
      </div>
      <PrintHeader
        title={t('audience.title')}
        scope={page.showAll ? t('looker.allWorkspaces') : page.workspaceName ?? null}
        dataDate={state.dataDate}
      />
      <ControlBand page={page} state={state} exporter={exporter} />
      <PrintLines state={state} />
      <div
        className="space-y-4 [-webkit-print-color-adjust:exact] [print-color-adjust:exact]"
        style={exporter.framing ? { width: PRINT_FRAME_WIDTH_PX, maxWidth: '100%' } : undefined}
      >
        <style>{PRINT_CSS}</style>
        <AudienceBody page={page} state={state} />
      </div>
      <AudienceChat page={page} state={state} />
    </div>
  );
};

export default AudienceTargeting;
